import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
  LineChart, Line, CartesianGrid,
} from 'recharts';
import { getAnalyticsCharts } from '../../services/api';
import './DashboardCommon.css';
import smLogo from '../../assets/SM_logo-removebg.png';
import ThemeToggle from '../ThemeToggle';

const CHART_COLORS = ['#b60000', '#10b981', '#f59e0b', '#3b82f6', '#6b7280', '#8b5cf6'];

function AnalyticsDashboard({ user, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [profileDropdown, setProfileDropdown] = useState(false);
  const [dashboardData, setDashboardData] = useState({ stats: null, charts: null });
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);

  useEffect(() => {
    if (activeMenu !== 'dashboard') return;
    let cancelled = false;
    setDashboardLoading(true);
    setDashboardError(null);
    getAnalyticsCharts()
      .then((res) => {
        if (!cancelled && res.success) {
          setDashboardData({ stats: res.stats || {}, charts: res.charts || {} });
        }
      })
      .catch((err) => { if (!cancelled) setDashboardError(err.message); })
      .finally(() => { if (!cancelled) setDashboardLoading(false); });
    return () => { cancelled = true; };
  }, [activeMenu]);

  const menuItems = [
    { id: 'dashboard', label: 'Overview', icon: 'home' },
    { id: 'performance', label: 'Performance', icon: 'trending' },
    { id: 'revenue', label: 'Revenue', icon: 'dollar' },
    { id: 'users', label: 'User Analytics', icon: 'users' },
    { id: 'reports', label: 'Custom Reports', icon: 'file' },
    { id: 'export', label: 'Export Data', icon: 'download' },
  ];

  const statsFromApi = dashboardData.stats;
  const stats = statsFromApi
    ? [
        { label: 'Total Users', value: String(statsFromApi.total_users ?? '–'), change: 'Active', color: '#10b981' },
        { label: 'Participating Tenders', value: String(statsFromApi.tender_participating ?? '–'), change: 'Active', color: '#3b82f6' },
        { label: 'Tender Discovery', value: String(statsFromApi.tender_discovery ?? '–'), change: 'Stage', color: '#f59e0b' },
        { label: 'Upcoming Deadlines', value: String(statsFromApi.upcoming_deadlines ?? '–'), change: '7 days', color: '#b60000' },
      ]
    : [
        { label: 'Total Users', value: '–', change: 'Active', color: '#10b981' },
        { label: 'Participating Tenders', value: '–', change: 'Active', color: '#3b82f6' },
        { label: 'Tender Discovery', value: '–', change: 'Stage', color: '#f59e0b' },
        { label: 'Upcoming Deadlines', value: '–', change: '7 days', color: '#b60000' },
      ];

  const renderIcon = (name) => {
    const icons = {
      home: <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>,
      trending: <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></>,
      dollar: <><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></>,
      users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></>,
      file: <><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></>,
      download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></>,
      logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></>,
      menu: <><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></>,
      bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></>,
    };
    return icons[name] || null;
  };

  return (
    <div className="dashboard-layout">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)} data-tooltip={sidebarOpen ? 'Collapse' : 'Expand'}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {sidebarOpen ? (
              <path d="M15 18l-6-6 6-6" />
            ) : (
              <path d="M9 18l6-6-6-6" />
            )}
          </svg>
        </button>

        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <button
              key={item.id}
              className={`nav-item ${activeMenu === item.id ? 'active' : ''}`}
              onClick={() => setActiveMenu(item.id)}
              data-tooltip={item.label}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {renderIcon(item.icon)}
              </svg>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      {/* Top Bar - Full Width */}
      <header className="topbar">
        <div className="topbar-left">
          <img src={smLogo} alt="SM Systems" className="topbar-logo" />
          <div className="topbar-divider"></div>
          <h1 className="page-title">Analytics Dashboard</h1>
        </div>

        <div className="topbar-right">
          <ThemeToggle />
          <div className="user-profile-wrapper">
            <button className="user-profile" onClick={() => setProfileDropdown(!profileDropdown)}>
              <div className="avatar">{user?.name?.charAt(0) || 'A'}</div>
              <div className="user-info">
                <span className="user-name">{user?.name || 'Analyst'}</span>
                <span className="user-role">Analytics User</span>
              </div>
              <svg className={`dropdown-arrow ${profileDropdown ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {profileDropdown && (
              <div className="profile-dropdown">
                <button className="dropdown-item" onClick={onLogout}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {renderIcon('logout')}
                  </svg>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={`main-content ${sidebarOpen ? '' : 'expanded'}`}>
        <div className="content">
          <div className="content-title-area">
            <nav className="breadcrumb" aria-label="Breadcrumb">
              <span>Analytics</span>
            </nav>
            <h2 className="content-page-title">Dashboard</h2>
          </div>
          <div className="content-header">
            <h2>Analytics Overview</h2>
            <p>Real-time insights and performance metrics.</p>
          </div>

          {dashboardError && (
            <div className="error-message" style={{ marginBottom: 16 }}>
              {dashboardError}
              <button type="button" onClick={() => setDashboardError(null)}>×</button>
            </div>
          )}

          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-content">
                  <span className="stat-label">{stat.label}</span>
                  <span className="stat-value">{dashboardLoading ? '…' : stat.value}</span>
                  <span className="stat-change" style={{ color: stat.color }}>{stat.change}</span>
                </div>
              </div>
            ))}
          </div>

          {!dashboardLoading && dashboardData.charts && (
            <div className="charts-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24, marginBottom: 24 }}>
              {dashboardData.charts.users_by_role && dashboardData.charts.users_by_role.length > 0 && (
                <div className="chart-card">
                  <h3>Users by Role</h3>
                  <div style={{ height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={dashboardData.charts.users_by_role}
                          dataKey="count"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ name, count }) => `${name}: ${count}`}
                        >
                          {dashboardData.charts.users_by_role.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              {dashboardData.charts.tenders_by_stage && dashboardData.charts.tenders_by_stage.length > 0 && (
                <div className="chart-card">
                  <h3>Tenders by Stage</h3>
                  <div style={{ height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dashboardData.charts.tenders_by_stage}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#b60000" name="Count" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              {dashboardData.charts.tenders_over_time && dashboardData.charts.tenders_over_time.length > 0 && (
                <div className="chart-card" style={{ gridColumn: '1 / -1' }}>
                  <h3>Tenders Over Time (last 6 months)</h3>
                  <div style={{ height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={dashboardData.charts.tenders_over_time}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="count" stroke="#b60000" name="Total" strokeWidth={2} dot={{ r: 4 }} />
                        <Line type="monotone" dataKey="participating" stroke="#10b981" name="Participating" strokeWidth={2} dot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="placeholder-section">
            <div className="placeholder-card">
              <h3>Performance Metrics</h3>
              <p>Detailed performance analysis and KPIs.</p>
              <button className="placeholder-btn">Coming Soon</button>
            </div>
            <div className="placeholder-card">
              <h3>User Behavior</h3>
              <p>User engagement and behavior patterns.</p>
              <button className="placeholder-btn">Coming Soon</button>
            </div>
            <div className="placeholder-card">
              <h3>Custom Reports</h3>
              <p>Generate and export custom reports.</p>
              <button className="placeholder-btn">Coming Soon</button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default AnalyticsDashboard;
